// Today's timeline with the running timer (architecture sprint-04 §6.1, S6.2). Project colours are
// inline data; with no project the muted token class is used.
import { instantToLocal, SECONDS_PER_DAY, type IanaZone, type LocalDate } from '@shared/calendar';
import { NO_PROJECT_COLOR, NO_PROJECT_LABEL } from '@shared/palette';
import type { CSSProperties } from 'react';
import { ChevronRight, Clock } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { SectionTitle } from '@/components/SectionTitle';
import { Card } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/cn';
import { layoutTimeline, type RunningInput, type TimelineBlock } from '@/lib/dashboard-model';
import { formatClock, formatDurationHm } from '@/lib/format';
import { serverNowMs } from '@/lib/timer-time';
import { useNowTick } from '@/lib/use-now';
import type { EntryListItem } from '@/types/entries';
import type { TimerSnapshot } from '@/types/timer';

const TICK_MS = 1000;
const HOUR_TICKS = [0, 3, 6, 9, 12, 15, 18, 21, 24];
const SECONDS_PER_HOUR = 3600;
const PERCENT = 100;
const MIN_BLOCK_WIDTH = '2px';

interface TodayTimelineProps {
  entries: EntryListItem[];
  snapshot: TimerSnapshot | null;
  today: LocalDate;
  tz: IanaZone;
  onEdit: (entry: EntryListItem) => void;
}

function pct(seconds: number): string {
  return `${(seconds / SECONDS_PER_DAY) * PERCENT}%`;
}

function blockStyle(block: TimelineBlock): CSSProperties {
  return { left: pct(block.start), width: `max(${pct(block.end - block.start)}, ${MIN_BLOCK_WIDTH})` };
}

function clockLabel(second: number): string {
  return second >= SECONDS_PER_DAY ? '24:00' : formatClock(second);
}

function describe(block: TimelineBlock): string {
  const project = block.project?.name ?? NO_PROJECT_LABEL;
  const description = block.description ?? 'No description';
  return `${project}, ${description}, ${clockLabel(block.start)} to ${clockLabel(block.end)}, ${formatDurationHm(block.end - block.start)}`;
}

function toRunning(snapshot: TimerSnapshot | null, perfNow: number): RunningInput | null {
  if (!snapshot) return null;
  const { timer } = snapshot;
  return {
    segmentStartedAt: timer.segmentStartedAt,
    endMs: timer.pausedAt ? Date.parse(timer.pausedAt) : serverNowMs(snapshot, perfNow),
    paused: timer.pausedAt !== null,
    project: timer.project,
    description: timer.description,
  };
}

export function TodayTimeline({ entries, snapshot, today, tz, onEdit }: TodayTimelineProps): JSX.Element {
  const perfNow = useNowTick(TICK_MS);
  const { lanes } = layoutTimeline(entries, toRunning(snapshot, perfNow), today, tz);
  const nowLocal = instantToLocal(new Date(), tz);
  const nowSecond = nowLocal.date === today ? nowLocal.second : null;

  return (
    <section aria-labelledby="timeline-heading" className="mb-8">
      <SectionTitle
        id="timeline-heading"
        title="Today's timeline"
        hint="Your day's segments positioned by actual clock time — gaps reflect real breaks and untracked time."
      />
      <Card>
      {lanes.length === 0 ? (
        <EmptyState variant="inset" icon={Clock} title="Nothing logged today yet." />
      ) : (
        <div className="space-y-1">
          <div className="relative">
            {lanes.map((lane, i) => (
              <div key={i} className="relative h-8 overflow-hidden rounded-lg bg-muted">
                {lane.map((block) => (
                  <Block key={block.key} block={block} onEdit={onEdit} />
                ))}
              </div>
            ))}
            {nowSecond !== null && (
              <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 w-0.5 bg-primary" style={{ left: pct(nowSecond) }} />
            )}
          </div>
          <div aria-hidden="true" className="relative mt-1 h-4 text-xs text-muted-foreground">
            {HOUR_TICKS.map((h) => (
              <span key={h} className="absolute -translate-x-1/2 tabular-nums first:translate-x-0 last:-translate-x-full" style={{ left: pct(h * SECONDS_PER_HOUR) }}>
                {String(h).padStart(2, '0')}
              </span>
            ))}
          </div>
        </div>
      )}
      </Card>
    </section>
  );
}

function Block({ block, onEdit }: { block: TimelineBlock; onEdit: (entry: EntryListItem) => void }): JSX.Element {
  const color = block.project?.color ?? null;
  if (block.running) {
    const hex = color ?? NO_PROJECT_COLOR;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            role="img"
            aria-label={block.paused ? 'Paused timer' : 'Running timer'}
            className="absolute inset-y-0 rounded-sm"
            style={{
              ...blockStyle(block),
              backgroundImage: `repeating-linear-gradient(45deg, ${hex} 0 6px, transparent 6px 10px)`,
              border: `1px solid ${hex}`,
            }}
          />
        </TooltipTrigger>
        <TooltipContent>{block.paused ? 'Paused timer' : 'Running timer'}</TooltipContent>
      </Tooltip>
    );
  }
  const entry = block.entry;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`${describe(block)}. Edit`}
          onClick={() => entry && onEdit(entry)}
          className={cn(
            'absolute inset-y-0 flex items-center justify-end rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            !color && 'bg-muted-foreground/40',
          )}
          style={{ ...blockStyle(block), ...(color ? { backgroundColor: color } : {}) }}
        >
          {block.continues && <ChevronRight aria-hidden="true" className="h-3 w-3 text-background" />}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p className="font-medium">{block.project?.name ?? NO_PROJECT_LABEL}</p>
        {block.description && <p>{block.description}</p>}
        <p className="tabular-nums text-muted-foreground">
          {`${clockLabel(block.start)}–${clockLabel(block.end)}, ${formatDurationHm(block.end - block.start)}`}
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
